import React, {
  createContext,
  useContext,
  ReactNode,
  useRef,
  useEffect,
} from "react";
import { iGlobalContext } from "../globalContext/interface";
import { iHistoryContext } from "../historyContext/interface";
import { useHistoryContext } from "../historyContext/historyContext";
import { useNavigate } from "react-router-dom";
import { StateContext } from "../globalContext/context";
import { chatScrollBus } from "../../services/chatScroll/chatScrollBus";
import { toastMessage } from "../../utils/ToastProvider";
import { iChatData } from "../../types";
import { useSocketContext } from "../socketContext/SocketContext";
import { v2orch } from "../../utils/v2OrchLog";
import { dropEmptyContentMessages } from "../../streaming/finalizeGuards";
import { getChatId, setChatId } from "../../utils/chat/chatId";
import { rotateChatId } from "../../utils/chat/rotateChatId";
import { isSelectionActiveInChat } from "../../utils/chat/chatSelection";
import { withCheckoutNotes } from "../../utils/chat/checkoutNotes";
import { SELECTED_CHAT_DATA } from "../../constant/dummyData";
import { getSessionId } from "../../utils/sessionId";
import { SearchProgressStore } from "../../services/searchProgress/searchProgressStore";
import { isStreamedFlightClarifyError } from "../../services/searchProgress/legacyFlightError";
import { rollItineraryDatesToFuture } from "../../utils/chat/rollItineraryDatesToFuture";
import { ROUTES } from "../../constant";
import { portalNavigate } from "../../utils/embedRouteRewrite";
import { ScreenTypes } from "../../enums";
import SpeechRecognition from "react-speech-recognition";
import { v4 } from "uuid";
import { iDataStreamingContext } from "../dataStreamingContext/dataStreamingContextInterface";
import { useDataStreamingContext } from "../dataStreamingContext/DataStreamingContext";
import toast from "react-hot-toast";
import {
  useTopDestinationContext,
  sanitizeItineraryForLLM,
} from "../topDestinationContext/TopDestinationContext";
import { GA_LABEL_ENUMS, triggerGAEvent } from "../../constant/google-analytics/GAEvents";
import { useTheresiasEventBridge } from "../../services/intent/useTheresiasEventBridge";
import { voiceBus, voiceTurnMarks } from "../../services/voice/voiceBus";
export interface iPocContext {
  handleNewTripSendMessage: any;
  handleTopDestinationSendMessage: any;
}

export interface iSendOptions {
  voice?: boolean;
}
// V2-block helpers (extractAiAssistantTexts / findRenderPayload /
// normalizeHotelResponse) moved to `src/utils/v2BlockRenderer.ts` so both
// pocContext (legacy HTTP fallback, no current callers) and
// DataStreamingContext (WS v2_block finalize) share one implementation.

// Walks chatData[] backwards and returns the most recent orchestrator-decided
// `agents[0].key` (poc_chat / conversation_stream_new). Skips bypass + sibling
// keys (modify_itinerary_stream / show_more / chat_system_explore_city_msg /
// nearby_activity / nearby_restaurants / activity_details / askzoya_*). Returns
// null on cold start or when no orchestrator-decided turn has happened yet.
// Used to populate `priorAgentKey` in the outbound WS body — drives the
// orchestrator's short-continuation stickiness ("yes the cheaper one" after a
// poc_chat turn correctly sticks on poc_chat even though it's ambiguous).
const ORCH_DECIDED_KEYS = new Set(["poc_chat", "conversation_stream_new"]);
const ORCH_SKIPPED_KEYS = new Set([
  "modify_itinerary_stream",
  "show_more",
  "chat_system_explore_city_msg",
  "nearby_activity",
  "nearby_restaurants",
  "activity_details",
  "askzoya_hotels",
  "askzoya_flights",
]);
function derivePriorAgentKey(chatHistory: iChatData[] | undefined): {
  priorAgentKey: string | null;
  walkedEntries: number;
  skippedKeys: string[];
} {
  if (!Array.isArray(chatHistory) || chatHistory.length === 0) {
    return { priorAgentKey: null, walkedEntries: 0, skippedKeys: [] };
  }
  const skippedKeys: string[] = [];
  let walked = 0;
  for (let i = chatHistory.length - 1; i >= 0; i--) {
    walked++;
    const key = chatHistory[i]?.agents?.[0]?.key;
    if (!key) continue;
    if (ORCH_DECIDED_KEYS.has(key)) {
      return { priorAgentKey: key, walkedEntries: walked, skippedKeys };
    }
    if (ORCH_SKIPPED_KEYS.has(key)) {
      skippedKeys.push(key);
      continue;
    }
  }
  return { priorAgentKey: null, walkedEntries: walked, skippedKeys };
}

export function deriveActiveItinerary(selectedChatData: any): {
  hasActiveItinerary: string | null;
  cityCount: number;
} {
  const cityWise: any[] = Array.isArray(selectedChatData?.city_wise_itinerary)
    ? selectedChatData.city_wise_itinerary
    : [];
  const destinations: any[] = Array.isArray(selectedChatData?.destinationList)
    ? selectedChatData.destinationList
    : [];
  const looksLikeItinerary =
    cityWise.length > 0 ||
    destinations.some(
      (c: any) =>
        (Array.isArray(c?.tripDetails) && c.tripDetails.length > 0) ||
        (typeof c?.city_name === "string" && c.city_name.length > 0)
    );
  if (!looksLikeItinerary) return { hasActiveItinerary: null, cityCount: 0 };
  const source = cityWise.length > 0 ? cityWise : destinations;
  const cities: string[] = source
    .map((c: any) => c?.city_name || c?.destination_city || c?.destination_name)
    .filter((n: any): n is string => typeof n === "string" && n.length > 0);
  if (cities.length === 0) return { hasActiveItinerary: null, cityCount: 0 };
  return { hasActiveItinerary: cities.join(", "), cityCount: cities.length };
}

function deriveActiveArtifacts(chatData: any): {
  hasActiveFlights: boolean;
  hasActiveStays: boolean;
} {
  let hasActiveFlights = false;
  let hasActiveStays = false;
  if (Array.isArray(chatData)) {
    for (const entry of chatData) {
      const flightResponse = entry?.flightResponse;
      if (
        flightResponse &&
        Array.isArray(flightResponse.sectors) &&
        flightResponse.sectors.length > 0 &&
        !isStreamedFlightClarifyError(entry)
      ) {
        hasActiveFlights = true;
      }
      const hotelResponse = entry?.hotelResponse;
      if (
        Array.isArray(hotelResponse) &&
        hotelResponse.some(
          (city: any) => Array.isArray(city?.hotels) && city.hotels.length > 0
        )
      ) {
        hasActiveStays = true;
      }
      if (hasActiveFlights && hasActiveStays) break;
    }
  }
  return { hasActiveFlights, hasActiveStays };
}

const PocContext = createContext({} as iPocContext);
export const PocContextProvider = ({ children }: { children: ReactNode }) => {
  const context = useContext(StateContext);
  const {
    chatInputText,
    setChatInputText,
    setShowMoreFlag,
    setOpenOverlayScreen,
    setChatData,
    setFirstScreen,
    setenableMultiSelect,
    setScreen,
    selectedChatData,
    setSelectedChatData,
    isMobile,
    chatData,
    userInfo,
    accessJWT,
  }: iGlobalContext = context;
  const {
    setCurrentHistoryId,
  }: iHistoryContext = useHistoryContext();
  useTopDestinationContext();
  const {
    disableInput,
    setDisableInput,
    chatAbortControllerRef,
    outboundV2RequestRef,
  }: iDataStreamingContext = useDataStreamingContext();
  const { handleOpenSocketConnection } = useSocketContext();
  const navigate = useNavigate();
  const fireTheresiasEvent = useTheresiasEventBridge();

  const sendInFlightRef = useRef(false);
  useEffect(() => {
    if (!disableInput) sendInFlightRef.current = false;
  }, [disableInput]);

  // Main-chat send for a brand-new conversation. WS-only as of v2 orchestrator
  // wiring — payload goes to the LLM-function Lambda's `itinerary` requestType
  // handler, which either invokes the orchestrator (when origin is allowlisted)
  // or runs byte-equivalent to today. The Lambda streams the response back
  // over WS as `v2_block` frames (poc_chat route) or legacy intent-envelope
  // frames (conversation_stream_new route); both are finalized by
  // DataStreamingContext, which stamps `agents` + `router` + `traceId` on the
  // pending chat entry by id.
  async function handleNewTripSendMessage(
    promptOverride?: string,
    hideUserBubble?: boolean,
    options?: iSendOptions,
  ) {
    setenableMultiSelect(false);
    const effectivePrompt = promptOverride ?? chatInputText;
    if (effectivePrompt.trim() === "") {
      toastMessage.info("Please type your message!");
      return;
    }

    if (sendInFlightRef.current) return;
    sendInFlightRef.current = true;

    rotateChatId();
    setCurrentHistoryId("");
    // Theresias `home-chat-send` event. Wired here (active path) — the
    // legacy wire in `TopDestinationContext.handleSendMessageStreaming`
    // is unreachable in the current UI flow.
    triggerGAEvent(GA_LABEL_ENUMS.chat_input_field_send_prompt_to_llm_click);
    fireTheresiasEvent("home-chat-send", { userMessage: effectivePrompt });
    setOpenOverlayScreen(true);
    setDisableInput(true);
    SpeechRecognition.stopListening();
    setShowMoreFlag(false);
    const abortController = new AbortController();
    chatAbortControllerRef.current = abortController;

    const reqId = v4();
    const traceId = reqId.slice(0, 8);
    const userEntryId = v4();
    const pendingEntryId = v4();

    // New turn = clean slate for the (purely Pusher-triggered) search-progress
    // stream. The prior search's done-state is already frozen onto its entry
    // (ChatContentNew), so this clears only the global store — preventing a stale
    // done-state from leaking onto this turn's bubble if no search events arrive.
    SearchProgressStore.reset();
    const voiceTurn = options?.voice === true;

    // Replace chatData with [userBubble, loadingPlaceholder]. Finalize step
    // (DataStreamingContext) replaces the loading entry by id.
    setChatData([
      {
        id: userEntryId,
        message: effectivePrompt,
        isUserMessage: true,
        prompt: [],
        isLoading: false,
        destinationList: [],
        singleSelectedDestination: false,
        shouldNotShowFirstMessage: hideUserBubble === true,
        ...voiceTurnMarks(voiceTurn),
      },
      {
        id: pendingEntryId,
        message: "",
        prompt: [],
        isUserMessage: false,
        isLoading: true,
        destinationList: [],
        singleSelectedDestination: false,
      },
    ]);
    chatScrollBus.send(pendingEntryId);
    setSelectedChatData({ ...SELECTED_CHAT_DATA });
    setChatInputText("");

    // Navigate up front so the loading placeholder lands on the chat page.
    if (isMobile) portalNavigate(navigate, ROUTES.TopDestination);
    else setFirstScreen(ScreenTypes.topDestination);
    setScreen("");

    const messages = [{ role: "user", content: effectivePrompt }];
    const origin = window.location.origin;
    const newConversation: iChatData[] = [];
    const { priorAgentKey, walkedEntries, skippedKeys } = derivePriorAgentKey(newConversation);
    const { hasActiveItinerary, cityCount } = deriveActiveItinerary(null);
    const { hasActiveFlights, hasActiveStays } = deriveActiveArtifacts(newConversation);

    v2orch("turn-start", {
      traceId,
      isNewTrip: true,
      userInputPreview: effectivePrompt.slice(0, 80),
      chatDataLen: chatData.length,
      pendingEntryId,
    });
    v2orch("derive.priorAgent", { traceId, priorAgentKey, walkedEntries, skippedKeys });
    v2orch("derive.activeItin", {
      traceId,
      hasActiveItinerary,
      cityCount,
      source: "new-conversation",
    });
    v2orch("derive.origin", { traceId, origin });
    v2orch("derive.auth", { traceId, hasJWT: !!accessJWT, jwtLen: accessJWT?.length ?? 0 });

    // Stash for DataStreamingContext.finalizeV2Turn — drives target chat entry
    // by id and the suggestion-fetch promptArray.
    outboundV2RequestRef.current = {
      messages,
      reqId,
      traceId,
      pendingEntryId,
      caller: "newTrip",
    };

    const body = {
      requestType: "itinerary" as const,
      chatId: getChatId() || "",
      iteneryInput: null,
      userLocation: null,
      userInput: effectivePrompt,
      messages,
      userId: userInfo?.id ? String(userInfo.id) : "",
      accessToken: accessJWT,
      origin,
      priorAgentKey,
      hasActiveItinerary,
      hasActiveFlights,
      hasActiveStays,
      traceId,
      // BE publishes streamed hotel search-progress to private-search-${sessionId};
      // same id the FE subscribes with (searchProgressChannel). Additive only.
      sessionId: getSessionId(),
      ...(voiceTurn ? { voice: true } : {}),
    };
    v2orch("ws.send", {
      traceId,
      requestType: body.requestType,
      bodyKeys: Object.keys(body),
      bodyPreview: {
        origin,
        priorAgentKey,
        hasActiveItinerary,
        hasActiveFlights,
        hasActiveStays,
        userInputLen: effectivePrompt.length,
        messagesLen: messages.length,
        sessionId: body.sessionId,
      },
    });
    if (!voiceTurn && !hideUserBubble) voiceBus.typedTurn({ text: effectivePrompt, traceId });
    handleOpenSocketConnection(body);
    return { traceId, userEntryId, pendingEntryId };
  }

  // Main-chat send for a continuation turn. Same payload shape as the new-trip
  // send; only difference is messages include prior turn context and the user
  // bubble is appended to (not replacing) chatData.
  //
  // `hideUserBubble` (mirrors `handleNewTripSendMessage`) suppresses the DISPLAY
  // of the user bubble only — the prompt is still sent to the LLM in `messages`.
  // For internal/system prompts the user never typed, e.g. the expired-results
  // dialog's "fetch refreshed hotel results". Do NOT use it for synthesized
  // prompts that legitimately describe the user's intent ("Find stays in Paris
  // from …", the in-chat select-card prompts) — those should stay visible.
  async function handleTopDestinationSendMessage(
    promptOverride?: string,
    hideUserBubble?: boolean,
    attachItinerary?: boolean,
    options?: iSendOptions,
  ) {
    toast.dismiss();
    if (chatData.length > 0 && chatData[chatData.length - 1].isLoading) {
      toastMessage.info("Please wait, we are still fetching the result!");
      return;
    }
    const effectivePrompt = promptOverride ?? chatInputText;
    if (effectivePrompt.trim() === "") {
      toastMessage.info("Please type your message!");
      return;
    }

    if (sendInFlightRef.current) return;
    sendInFlightRef.current = true;

    if (!getChatId()) {
      setChatId(v4());
    }
    // Theresias `home-chat-send` event. Wired here (active path) — the
    // legacy wire in `TopDestinationContext.handleSendMessageStreaming`
    // is unreachable in the current UI flow.
    triggerGAEvent(GA_LABEL_ENUMS.chat_input_field_send_prompt_to_llm_click);
    fireTheresiasEvent("home-chat-send", { userMessage: effectivePrompt });

    const reqId = v4();
    const traceId = reqId.slice(0, 8);
    const userEntryId = v4();
    const pendingEntryId = v4();

    // New turn = clean slate for the (purely Pusher-triggered) search-progress
    // stream (prior search already frozen onto its entry; this clears only the
    // global store so a stale done-state can't leak onto this turn's bubble).
    SearchProgressStore.reset();
    const voiceTurn = options?.voice === true;

    setChatData((prev: iChatData[]) => [
      ...prev,
      {
        id: userEntryId,
        message: effectivePrompt,
        isUserMessage: true,
        prompt: [],
        isLoading: false,
        destinationList: [],
        singleSelectedDestination: false,
        shouldNotShowFirstMessage: hideUserBubble === true,
        ...voiceTurnMarks(voiceTurn),
      },
      {
        id: pendingEntryId,
        message: "",
        prompt: [],
        isUserMessage: false,
        isLoading: true,
        destinationList: [],
        singleSelectedDestination: false,
      },
    ]);
    setChatInputText("");
    chatScrollBus.send(pendingEntryId);
    setDisableInput(true);
    const abortController = new AbortController();
    chatAbortControllerRef.current = abortController;

    // Walk chatData history backwards for the most recent assistant context.
    const dupChatData: any[] = JSON.parse(JSON.stringify(chatData));
    const promptContext: any[] =
      dupChatData.reverse().find((item: any) => item?.prompt?.length > 0)?.prompt || [];

    const messages = dropEmptyContentMessages([
      ...withCheckoutNotes(chatData, promptContext),
      { role: "user", content: effectivePrompt },
    ]);
    const origin = window.location.origin;
    const { priorAgentKey, walkedEntries, skippedKeys } = derivePriorAgentKey(chatData);
    const turnChatId = getChatId();
    const selectionActive = isSelectionActiveInChat(selectedChatData, chatData, turnChatId);
    const activeItinerary = attachItinerary === true || selectionActive ? selectedChatData : null;
    if (attachItinerary === true && !selectionActive && selectedChatData?.id) {
      const attachedId = selectedChatData.id;
      setSelectedChatData((prev: any) =>
        prev?.id === attachedId ? { ...prev, attachedToChatId: turnChatId } : prev,
      );
    }
    const { hasActiveItinerary, cityCount } = deriveActiveItinerary(activeItinerary);
    const { hasActiveFlights, hasActiveStays } = deriveActiveArtifacts(chatData);

    v2orch("turn-start", {
      traceId,
      isNewTrip: false,
      userInputPreview: effectivePrompt.slice(0, 80),
      chatDataLen: chatData.length,
      pendingEntryId,
    });
    v2orch("derive.priorAgent", { traceId, priorAgentKey, walkedEntries, skippedKeys });
    v2orch("derive.activeItin", {
      traceId,
      hasActiveItinerary,
      cityCount,
      selectionActive,
      source: "selectedChatData.destinationList||city_wise_itinerary",
    });
    v2orch("derive.origin", { traceId, origin });
    v2orch("derive.auth", { traceId, hasJWT: !!accessJWT, jwtLen: accessJWT?.length ?? 0 });

    outboundV2RequestRef.current = {
      messages,
      reqId,
      traceId,
      pendingEntryId,
      caller: "topDestination",
    };

    const currentItineray = hasActiveItinerary
      ? rollItineraryDatesToFuture(sanitizeItineraryForLLM(activeItinerary))
      : undefined;
    v2orch("derive.currentItinerary", {
      traceId,
      attachItineraryRequested: !!attachItinerary,
      selectionActive,
      currentItineraryIncluded: !!currentItineray,
    });

    const body = {
      ...(currentItineray ? { currentItineray } : {}),
      requestType: "itinerary" as const,
      chatId: getChatId() || "",
      iteneryInput: null,
      userLocation: null,
      userInput: effectivePrompt,
      messages,
      userId: userInfo?.id ? String(userInfo.id) : "",
      accessToken: accessJWT,
      origin,
      priorAgentKey,
      hasActiveItinerary,
      hasActiveFlights,
      hasActiveStays,
      traceId,
      // BE publishes streamed hotel search-progress to private-search-${sessionId};
      // same id the FE subscribes with (searchProgressChannel). Additive only.
      sessionId: getSessionId(),
      ...(voiceTurn ? { voice: true } : {}),
    };
    v2orch("ws.send", {
      traceId,
      requestType: body.requestType,
      bodyKeys: Object.keys(body),
      bodyPreview: {
        origin,
        priorAgentKey,
        hasActiveItinerary,
        hasActiveFlights,
        hasActiveStays,
        userInputLen: effectivePrompt.length,
        messagesLen: messages.length,
        sessionId: body.sessionId,
      },
    });
    if (!voiceTurn && !hideUserBubble) voiceBus.typedTurn({ text: effectivePrompt, traceId });
    handleOpenSocketConnection(body);
    return { traceId, userEntryId, pendingEntryId };
  }

  const value = {
    handleNewTripSendMessage,
    handleTopDestinationSendMessage,
  };
  return <PocContext.Provider value={value}>{children}</PocContext.Provider>;
};
export const usePocContext = () => useContext(PocContext);
